import Image, { type ImageProps } from 'next/image'

const TRUSTED_HOSTNAMES = new Set([
  'iguyomegtpcyoeqklhfn.supabase.co',
  'eqgmhrxbnwahhfuhjacs.supabase.co',
  'images.pexels.com',
  'assets.skulytics.io',
  'images.unsplash.com',
])

function isTrustedSrc(src: ImageProps['src']): boolean {
  if (typeof src !== 'string') return true
  try {
    return TRUSTED_HOSTNAMES.has(new URL(src).hostname)
  } catch {
    return true // relative URLs are trusted
  }
}

/**
 * Drop-in replacement for next/image that renders a plain <img> for
 * external URLs not in our trusted allowlist, avoiding the Next.js
 * image-optimization proxy (SSRF risk). Trusted URLs still go through
 * the optimizer.
 */
export function SafeImage({
  src,
  alt,
  fill,
  className,
  style,
  width,
  height,
  sizes: _sizes,
  quality: _quality,
  priority: _priority,
  placeholder: _placeholder,
  blurDataURL: _blurDataURL,
  loader: _loader,
  onLoad: _onLoad,
  onError: _onError,
  ...rest
}: ImageProps) {
  if (typeof src === 'string' && !isTrustedSrc(src)) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt ?? ''}
        className={className}
        style={
          fill
            ? { position: 'absolute', inset: 0, width: '100%', height: '100%', ...style }
            : style
        }
        width={fill ? undefined : (width as number)}
        height={fill ? undefined : (height as number)}
        onLoad={_onLoad}
        onError={_onError}
        {...rest}
      />
    )
  }

  return (
    <Image
      src={src}
      alt={alt ?? ''}
      fill={fill}
      className={className}
      style={style}
      width={width}
      height={height}
      sizes={_sizes}
      quality={_quality}
      priority={_priority}
      placeholder={_placeholder}
      blurDataURL={_blurDataURL}
      loader={_loader}
      onLoad={_onLoad}
      onError={_onError}
      {...rest}
    />
  )
}
